// src/components/DemoPage.jsx
// Full interactive demo — mirrors of real dashboard exactly.
// Mounts mock API as window.__demoAPI and renders the real component tree.

import { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { mockElectronAPI } from '../utils/mockElectron'
import ConnectionCard from './ConnectionCard'
import ActionBar from './ConnectionActions'
import {
  playConnectionSound,
  playAlertSound,
  playCriticalSound,
  playStartupSound,
} from '../utils/sounds'

// Professional SVG icons for alerts
const AlertIcons = {
  warning: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/>
      <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  ),
  critical: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="8" x2="12" y2="12"/>
      <line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
  ),
  info: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="16" x2="12" y2="16"/>
      <line x1="12" y1="8" x2="12.01" y2="8"/>
    </svg>
  ),
  unencrypted: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2"/>
      <path d="M7 11V7a5 5 0 019.9-1"/>
      <circle cx="12" cy="16" r="1"/>
    </svg>
  ),
  network: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="21" x2="4" y2="14"/>
      <line x1="4" y1="10" x2="4" y2="3"/>
      <line x1="12" y1="21" x2="12" y2="12"/>
      <line x1="12" y1="8" x2="12" y2="3"/>
      <line x1="20" y1="21" x2="20" y2="16"/>
      <line x1="20" y1="12" x2="20" y2="3"/>
      <line x1="1" y1="14" x2="7" y2="14"/>
      <line x1="9" y1="8" x2="15" y2="8"/>
      <line x1="17" y1="16" x2="23" y2="16"/>
    </svg>
  ),
  desktop: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
      <line x1="8" y1="21" x2="16" y2="21"/>
      <line x1="12" y1="17" x2="12" y2="21"/>
    </svg>
  ),
  globe: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <line x1="2" y1="12" x2="22" y2="12"/>
      <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>
    </svg>
  ),
  location: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
      <circle cx="12" cy="10" r="3"/>
    </svg>
  ),
}

// Map alert rule IDs to appropriate icons
const getAlertIcon = (ruleId, severity) => {
  const iconMap = {
    unencrypted_connection: AlertIcons.unencrypted,
    unusual_low_port: AlertIcons.warning,
    very_high_port: AlertIcons.info,
    unknown_location: AlertIcons.globe,
    flagged_country: AlertIcons.critical,
    remote_desktop: AlertIcons.desktop,
  }
  const IconComponent = iconMap[ruleId] || AlertIcons[severity] || AlertIcons.info
  return <IconComponent />
}

// ─── Tiny icon helper ─────────────────────────────────────────────────────────
const Ic = ({ size = 16, color = 'currentColor', sw = 1.8, children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round"
    style={{ display: 'block', flexShrink: 0 }}>{children}</svg>
)
const BackIcon  = () => <Ic size={15} sw={2}><polyline points="15 18 9 12 15 6"/></Ic>
const BellIcon  = () => <Ic size={14} sw={1.8}><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></Ic>
const GridIcon  = () => <Ic size={14} sw={1.8}><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></Ic>
const ListIcon  = () => <Ic size={14} sw={1.8}><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></Ic>
// ─── Mini waveform chart ───────────────────────────────────────────────────────────
function MiniChart({ dlHistory, ulHistory, dlSpeedBps, ulSpeedBps }) {
  const canvasRef = useRef(null)
  const animRef   = useRef(null)
  const smoothIn  = useRef(new Float32Array(30))
  const smoothOut = useRef(new Float32Array(30))
  const targetsIn = useRef(new Float32Array(30))
  const targetsOut = useRef(new Float32Array(30))

  useEffect(() => {
    // Use actual history data for targets
    const pIn  = [...new Array(Math.max(0, 30 - dlHistory.length)).fill(0), ...dlHistory].slice(-30)
    const pOut = [...new Array(Math.max(0, 30 - ulHistory.length)).fill(0), ...ulHistory].slice(-30)
    for (let i = 0; i < 30; i++) {
      targetsIn.current[i]  = pIn[i]  || 0
      targetsOut.current[i] = pOut[i] || 0
    }
  }, [dlHistory, ulHistory])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    canvas.width = canvas.offsetWidth || 300
    canvas.height = canvas.offsetHeight || 60

    const ctx = canvas.getContext('2d')
    let running = true
    let lastFrame = { current: 0 }

    function drawWave(ctx, smoothedIn, smoothedOut, max, H, W) {
      const halfH = H / 2
      
      // Center divider line
      ctx.beginPath()
      ctx.moveTo(0, halfH)
      ctx.lineTo(W, halfH)
      ctx.strokeStyle = 'rgba(255,255,255,0.06)'
      ctx.lineWidth = 1
      ctx.stroke()

      // In — blue, grows from center upward
      const ptsIn = Array.from(smoothedIn).map((v, i) => ({
        x: (i / 29) * W,
        y: halfH - (v / max) * halfH * 0.9,
      }))

      ctx.beginPath()
      ctx.moveTo(0, halfH)
      for (const p of ptsIn) ctx.lineTo(p.x, p.y)
      ctx.lineTo(W, halfH)
      ctx.closePath()
      ctx.fillStyle = 'rgba(77,157,224,0.2)'
      ctx.fill()

      ctx.beginPath()
      ctx.moveTo(ptsIn[0].x, ptsIn[0].y)
      for (let i = 1; i < ptsIn.length; i++) {
        const mx = (ptsIn[i-1].x + ptsIn[i].x) / 2
        ctx.quadraticCurveTo(ptsIn[i-1].x, ptsIn[i-1].y, mx, (ptsIn[i-1].y + ptsIn[i].y) / 2)
      }
      ctx.strokeStyle = '#4d9de0'
      ctx.lineWidth = 1.2
      ctx.globalAlpha = 0.9
      ctx.stroke()
      ctx.globalAlpha = 1

      // Out — red/coral, grows from center downward
      const ptsOut = Array.from(smoothedOut).map((v, i) => ({
        x: (i / 29) * W,
        y: halfH + (v / max) * halfH * 0.9,
      }))

      ctx.beginPath()
      ctx.moveTo(0, halfH)
      for (const p of ptsOut) ctx.lineTo(p.x, p.y)
      ctx.lineTo(W, halfH)
      ctx.closePath()
      ctx.fillStyle = 'rgba(239,100,100,0.18)'
      ctx.fill()

      ctx.beginPath()
      ctx.moveTo(ptsOut[0].x, ptsOut[0].y)
      for (let i = 1; i < ptsOut.length; i++) {
        const mx = (ptsOut[i-1].x + ptsOut[i].x) / 2
        ctx.quadraticCurveTo(ptsOut[i-1].x, ptsOut[i-1].y, mx, (ptsOut[i-1].y + ptsOut[i].y) / 2)
      }
      ctx.strokeStyle = '#ef6464'
      ctx.lineWidth = 1.2
      ctx.globalAlpha = 0.85
      ctx.stroke()
      ctx.globalAlpha = 1
    }

    function draw(ts) {
      if (!running) return
      if (ts - lastFrame.current < 100) { animRef.current = requestAnimationFrame(draw); return }
      if (document.hidden || !document.hasFocus()) { animRef.current = requestAnimationFrame(draw); return }
      lastFrame.current = ts

      const W = canvas.offsetWidth
      const H = canvas.offsetHeight
      if (canvas.width !== W || canvas.height !== H) { canvas.width = W; canvas.height = H }

      let dirty = false
      for (let i = 0; i < 30; i++) {
        const dIn  = (targetsIn.current[i]  - smoothIn.current[i])  * 0.12
        const dOut = (targetsOut.current[i] - smoothOut.current[i]) * 0.12
        if (Math.abs(dIn) > 0.5 || Math.abs(dOut) > 0.5) dirty = true
        smoothIn.current[i]  += dIn
        smoothOut.current[i] += dOut
      }
      if (!dirty) { animRef.current = requestAnimationFrame(draw); return }

      const maxIn  = Math.max(...smoothIn.current,  1)
      const maxOut = Math.max(...smoothOut.current, 1)
      const globalMax = Math.max(maxIn, maxOut)
      
      ctx.clearRect(0, 0, W, H)
      drawWave(ctx, smoothIn.current, smoothOut.current, globalMax, H, W)

      animRef.current = requestAnimationFrame(draw)
    }

    animRef.current = requestAnimationFrame(draw)
    return () => { running = false; cancelAnimationFrame(animRef.current) }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      width={300}
      height={60}
      style={{ display: 'block', width: '100%', height: 60 }}
    />
  )
}

// ─── Ring gauge ───────────────────────────────────────────────────────────────
function Ring({ pct, color, size = 40, stroke = 3.5 }) {
  const r    = (size - stroke * 2) / 2
  const circ = 2 * Math.PI * r
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={stroke} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={circ * (1 - pct / 100)}
        style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
    </svg>
  )
}

// ─── Alert row ────────────────────────────────────────────────────────────────
function AlertRow({ alert, onDismiss }) {
  if (alert.dismissed) return null
  const actionTarget = {
    appName: alert.appName || 'Unknown App',
    pid: alert.pid,
    remoteIP: alert.remoteIP,
    domain: alert.domain,
    country: alert.country,
  }

  return (
    <div style={{
      display: 'flex', gap: 12, padding: '14px 16px', marginBottom: 8,
      background: alert.severity === 'critical' ? 'rgba(239,68,68,0.06)' : 'rgba(245,166,35,0.05)',
      border: `1px solid ${alert.severity === 'critical' ? 'rgba(239,68,68,0.25)' : 'rgba(245,166,35,0.2)'}`,
      borderRadius: 10, alignItems: 'flex-start',
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: 8, flexShrink: 0, marginTop: 1,
        background: alert.severity === 'critical' ? 'rgba(239,68,68,0.12)' : 'rgba(245,166,35,0.12)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 16,
        color: alert.severity === 'critical' ? 'var(--accent-red)' : 'var(--accent-amber)',
      }}>
        {getAlertIcon(alert.ruleId, alert.severity)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 12, fontWeight: 600, marginBottom: 4,
          color: alert.severity === 'critical' ? 'var(--accent-red)' : 'var(--accent-amber)',
        }}>{alert.appName}</div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.55 }}>{alert.message}</div>
        <div style={{ fontSize: 10, color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)', marginTop: 6 }}>
          {new Date(alert.timestamp).toLocaleTimeString()}
        </div>
        <ActionBar conn={actionTarget} />
      </div>
      <button onClick={() => onDismiss(alert.id)}
        style={{ background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--text-tertiary)', fontSize: 16, padding: '0 2px', flexShrink: 0 }}>×</button>
    </div>
  )
}

// ─── DEMO PAGE ────────────────────────────────────────────────────────────────
export default function DemoPage() {
  const navigate = useNavigate()

  // Mount mock API as window.electronAPI so ConnectionCard (and its
  // Kill / Block / AI features) work identically to the real Electron build.
  useEffect(() => {
    const prev = window.electronAPI
    window.electronAPI = mockElectronAPI
    return () => { window.electronAPI = prev }
  }, [])

  const [speed,       setSpeed]       = useState({ downloadBps:0, uploadBps:0, downloadHuman:'0 B/s', uploadHuman:'0 B/s' })
  const [dlHistory,   setDlHistory]   = useState([])
  const [ulHistory,   setUlHistory]   = useState([])
  const [connections, setConnections] = useState([])
  const [metrics,     setMetrics]     = useState(null)
  const [alerts,      setAlerts]      = useState([])
  const [activeTab,   setActiveTab]   = useState('connections')
  const [search,      setSearch]      = useState('')

  const prevCount = useRef(0)

  useEffect(() => {
    const api = mockElectronAPI
    api.getConnections().then(conns => {
      setConnections(conns)
      prevCount.current = conns.length
      setTimeout(() => playStartupSound(), 600)
    }).catch(console.error)
    api.getSystemMetrics().then(setMetrics).catch(console.error)
    api.getAlertHistory().then(setAlerts).catch(console.error)

    const unsubs = [
      api.onSpeedUpdate(s => {
        setSpeed(s)
        setDlHistory(h => [...h, s.downloadBps].slice(-40))
        setUlHistory(h => [...h, s.uploadBps].slice(-40))
      }),
      api.onConnectionsUpdate(conns => {
        if (conns.length > prevCount.current) playConnectionSound()
        prevCount.current = conns.length
        setConnections(conns)
      }),
      api.onMetricsUpdate(setMetrics),
      api.onNewAlert(a => {
        setAlerts(prev => {
          if (prev.find(x => x.id === a.id)) return prev
          return [a, ...prev].slice(0, 50)
        })
        if (a.severity === 'critical') playCriticalSound()
        else playAlertSound()
      }),
    ]
    return () => unsubs.forEach(fn => fn?.())
  }, [])

  const dismissAlert = useCallback((id) => {
    setAlerts(prev => prev.filter(a => a.id !== id))
  }, [])

  const filtered = useMemo(() => connections.filter(c => {
    if (!search) return true
    const q = search.toLowerCase()
    return (c.appName||'').toLowerCase().includes(q)
      || (c.domain||'').toLowerCase().includes(q)
      || (c.country||'').toLowerCase().includes(q)
  }), [connections, search])

  const byApp = useMemo(() => Object.values(
    connections.reduce((acc, c) => {
      if (!acc[c.appName]) acc[c.appName] = { appName: c.appName, connections: [], hasWarning: false }
      acc[c.appName].connections.push(c)
      if (c.riskLevel === 'warning' || c.riskLevel === 'critical') acc[c.appName].hasWarning = true
      return acc
    }, {})
  ).sort((a, b) => b.connections.length - a.connections.length), [connections])

  const activeAlerts = useMemo(() => alerts.filter(a => !a.dismissed), [alerts])
  const criticalAlertCount = useMemo(
    () => activeAlerts.filter(a => a.severity === 'critical').length,
    [activeAlerts]
  )

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '90vh',
      maxWidth: 840, margin: '32px auto',
      background: '#080b12', color: '#dce8f5',
      fontFamily: 'var(--font-mono)', overflow: 'hidden',
      boxShadow: '0 0 0 1px rgba(255,255,255,0.08), 0 40px 100px rgba(0,0,0,0.7)',
      borderRadius: 14,
      // CSS vars used by ConnectionCard
      '--bg-elevated': '#0e1421',
      '--bg-surface': '#0d1220',
      '--border-default': 'rgba(255,255,255,0.07)',
      '--border-subtle': 'rgba(255,255,255,0.04)',
      '--text-primary': '#dce8f5',
      '--text-secondary': 'rgba(220,232,245,0.65)',
      '--text-tertiary': 'rgba(220,232,245,0.38)',
      '--accent-teal': '#00d4b8', '--accent-green': '#3ecf8e',
      '--accent-amber': '#f5a623', '--accent-red': '#ef4444',
      '--accent-blue': '#4d9de0', '--accent-cyan': '#22d3ee',
      '--font-mono': "'IBM Plex Mono', monospace",
      '--font-body': "'IBM Plex Sans', sans-serif",
    }}>
      <link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@300;400;500;600&family=IBM+Plex+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

      {/* ── TITLE BAR ── */}
      <div style={{
        height: 52, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 16px', flexShrink: 0,
        borderBottom: '1px solid rgba(255,255,255,0.07)',
        background: 'linear-gradient(to bottom, rgba(12,16,24,0.95), rgba(8,11,18,0.8))',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ padding: '2px 8px', borderRadius: 4, background: 'rgba(0,212,184,0.1)',
            border: '1px solid rgba(0,212,184,0.25)', fontSize: 9, fontFamily: 'var(--font-mono)',
            color: '#00d4b8', letterSpacing: '0.1em' }}>DEMO</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5,
            fontSize: 10, fontFamily: 'var(--font-mono)', color: '#00d4b8' }}>
            <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#00d4b8',
              boxShadow: '0 0 6px #00d4b8', animation: 'dp-pulse 2s ease-in-out infinite' }} />
            LIVE
          </div>
          <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.07)' }} />
          <button onClick={() => navigate('/')} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
            background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: 7, color: 'rgba(220,232,245,0.65)', fontSize: 12, cursor: 'pointer',
            fontFamily: 'var(--font-mono)', transition: 'all .15s',
          }}
            onMouseEnter={e => { e.currentTarget.style.background='rgba(255,255,255,0.08)'; e.currentTarget.style.color='#dce8f5' }}
            onMouseLeave={e => { e.currentTarget.style.background='rgba(255,255,255,0.04)'; e.currentTarget.style.color='rgba(220,232,245,0.65)' }}>
            <BackIcon />Back to site
          </button>
        </div>
      </div>

      {/* ── SPEED PANEL ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', flexShrink: 0, borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        {[
          ['↓ Download', speed.downloadHuman, '#4d9de0', true],
          ['↑ Upload',   speed.uploadHuman,   '#ef6464', false],
        ].map(([label, val, color, isFirst], i) => (
          <div key={label} style={{ padding: '12px 16px', borderRight: i===0 ? '1px solid rgba(255,255,255,0.07)' : 'none' }}>
            <div style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: 'var(--text-tertiary)',
              textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4 }}>{label}</div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 20, fontWeight: 500, color, letterSpacing: '-0.02em' }}>{val}</div>
          </div>
        ))}
        {/* Single dual-wave component spanning both columns */}
        <div style={{ gridColumn: '1 / -1', padding: '0 16px 12px' }}>
          <MiniChart dlHistory={dlHistory} ulHistory={ulHistory} dlSpeedBps={speed.downloadBps} ulSpeedBps={speed.uploadBps} />
        </div>
      </div>

      {/* ── METRICS BAR ── */}
      {metrics && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, padding: '8px 16px', flexShrink: 0,
          borderBottom: '1px solid rgba(255,255,255,0.07)', background: 'rgba(0,0,0,0.15)' }}>
          {[
            { label: 'CPU', pct: metrics.cpu.percentUsed, color: '#00d4b8', sub: `${metrics.cpu.loadAvg1m} load avg` },
            { label: 'RAM', pct: metrics.memory.percentUsed, color: '#4d9de0', sub: `${metrics.memory.usedGB} / ${metrics.memory.totalGB} GB` },
          ].map(({ label, pct, color, sub }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ position: 'relative', width: 40, height: 40 }}>
                <Ring pct={pct} color={color} />
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: 8, fontFamily: 'var(--font-mono)', fontWeight: 600, color }}>
                  {Math.round(pct)}%
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#dce8f5' }}>{label}</div>
                <div style={{ fontSize: 9, color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)' }}>{sub}</div>
              </div>
            </div>
          ))}
          <div style={{ marginLeft: 'auto', fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--text-tertiary)' }}>
            {connections.length} connections · {metrics.cpu.model}
          </div>
        </div>
      )}

      {/* ── TAB BAR ── */}
      <div style={{ display: 'flex', gap: 4, padding: '8px 16px', flexShrink: 0,
        borderBottom: '1px solid rgba(255,255,255,0.07)', background: 'rgba(0,0,0,0.1)' }}>
        {[
          { id: 'connections', label: 'Connections', icon: <ListIcon />, count: connections.length },
          { id: 'apps',        label: 'Apps',        icon: <GridIcon />, count: byApp.length },
          {
            id: 'alerts',
            label: 'Alerts',
            icon: <BellIcon />,
            count: activeAlerts.length,
            warn: activeAlerts.length > 0,
            critical: criticalAlertCount > 0,
          },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            title={t.critical ? `${criticalAlertCount} critical alert${criticalAlertCount === 1 ? '' : 's'} need attention` : undefined}
            style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px',
            borderRadius: 8, border: t.critical ? '1px solid rgba(239,68,68,0.55)' : '1px solid transparent', cursor: 'pointer',
            fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 500,
            background: t.critical ? 'rgba(239,68,68,0.14)' : activeTab === t.id ? 'rgba(77,157,224,0.1)' : 'transparent',
            color: t.critical ? 'var(--accent-red)' : activeTab === t.id ? '#dce8f5' : 'rgba(220,232,245,0.38)',
            borderBottom: activeTab === t.id ? '2px solid #4d9de0' : '2px solid transparent',
            boxShadow: t.critical ? '0 0 0 1px rgba(239,68,68,0.08), 0 0 18px rgba(239,68,68,0.28)' : 'none',
            animation: t.critical ? 'criticalTabPulse 1.25s ease-in-out infinite' : 'none',
            transition: 'background .15s, color .15s, border-color .15s, box-shadow .15s',
          }}>
            <span style={{
              display: 'flex',
              alignItems: 'center',
              animation: t.critical ? 'criticalBellRing 1.25s ease-in-out infinite' : 'none',
              transformOrigin: '50% 0%',
            }}>{t.icon}</span>
            {t.label}
            <div style={{ padding: '1px 6px', borderRadius: 4, fontSize: 9, fontFamily: 'var(--font-mono)',
              background: t.critical ? 'rgba(239,68,68,0.22)' : t.warn ? 'rgba(245,166,35,0.2)' : 'rgba(255,255,255,0.07)',
              color: t.critical ? '#ffb4b4' : t.warn ? '#f5a623' : 'rgba(220,232,245,0.38)',
              boxShadow: t.critical ? '0 0 10px rgba(239,68,68,0.35)' : 'none',
            }}>{t.count}</div>
          </button>
        ))}
        {activeTab === 'connections' && (
          <div style={{ marginLeft: 'auto', position: 'relative' }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search apps, domains…"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: 7, padding: '6px 10px 6px 28px', fontSize: 11,
                color: '#dce8f5', fontFamily: 'var(--font-mono)', outline: 'none', width: 180 }} />
            <div style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)' }}>
              <Ic size={12} color="rgba(220,232,245,0.38)" sw={2}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></Ic>
            </div>
          </div>
        )}
      </div>

      {/* ── CONTENT ── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px 24px' }}>
        {activeTab === 'connections' && (
          filtered.length === 0
            ? <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-tertiary)', fontSize: 13 }}>
                No connections match "{search}"
              </div>
            : <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {filtered.map((c, i) => <ConnectionCard key={c.id} connection={c} index={i} />)}
              </div>
        )}
        {activeTab === 'apps' && (
  <div style={{
    height: '100%', overflowY: 'auto',
    padding: '12px 16px 32px',
    display: 'flex', flexDirection: 'column', gap: 6,
  }}>
    {byApp.map(group => (
      <div key={group.appName}>
        <div style={{
          fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase',
          color: 'var(--accent-amber)', fontFamily: 'var(--font-mono)',
          padding: '0 4px', marginBottom: 4, marginTop: 8,
        }}>
          {group.appName} · {group.connections.length} {group.connections.length === 1 ? 'connection' : 'connections'}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {group.connections.map((conn, i) => (
            <ConnectionCard key={conn.id} connection={conn} index={i} />
          ))}
        </div>
      </div>
    ))}
  </div>
)}
        {activeTab === 'alerts' && (
          activeAlerts.length === 0
            ? <div style={{ display:'flex', alignItems:'center', justifyContent:'center',
                height:'100%', flexDirection:'column', gap:12, padding: '48px 0' }}>
                <div style={{ fontSize:40 }}>✅</div>
                <div style={{ color:'var(--text-secondary)', fontSize:15, fontWeight:500 }}>All clear</div>
                <div style={{ color:'var(--text-tertiary)', fontSize:12, textAlign:'center', maxWidth:300 }}>
                  Netwoke monitors your connections in real time. Any unusual activity will appear here.
                </div>
              </div>
            : activeAlerts.map(a => <AlertRow key={a.id} alert={a} onDismiss={() => dismissAlert(a.id)} />)
        )}
      </div>

      <style>{`
        @keyframes dp-pulse { 0%,100%{opacity:1;box-shadow:0 0 6px #00d4b8} 50%{opacity:.3;box-shadow:none} }
        @keyframes spin { to { transform: rotate(360deg); } }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 2px; }
      `}</style>
    </div>
  )
}
